import { signupAtomic } from '../services/api';
import { dispatchOwnerTokenEvent } from './ownerTokenEvents';

export type SignupProfile = {
  email?: string;
  password?: string;
  username?: string;
  id?: string;
  _id?: string;
  name?: string;
  phone?: string;
  company?: string;
  position?: string;
  location?: string;
  bio?: string;
  avatar?: string;
  avatarUrl?: string;
  coverUrl?: string;
  coverImage?: string;
  handle?: string;
  templateKey?: string;
  templateId?: string;
  templateMetaId?: string | null;
};

export function hasOwnerSessionLocal(): boolean {
  try {
    const token = localStorage.getItem('owner_token');
    const raw = localStorage.getItem('owner_session');
    if (!token || !raw) return false;
    const sess = JSON.parse(raw);
    return !!(sess?.id || sess?.username);
  } catch {
    return false;
  }
}

export function persistOwnerSession(
  token: string,
  user?: { id?: string; _id?: string; username?: string | null } | null,
) {
  try {
    localStorage.setItem('owner_token', token);
    localStorage.setItem(
      'owner_session',
      JSON.stringify({
        id: user?.id || user?._id || null,
        username: user?.username || null,
      }),
    );
    dispatchOwnerTokenEvent();
  } catch {}
}

async function validateOwnerToken(): Promise<boolean> {
  try {
    const token = localStorage.getItem('owner_token');
    if (!token) return false;
    const res = await fetch('/api/me', {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    if (!res.ok) return false;
    const data = await res.json();
    return !!(data?.id || data?.username);
  } catch {
    return false;
  }
}

async function tryOwnerLogin(email: string, password: string): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data?.token) {
      persistOwnerSession(data.token, data.user);
      return true;
    }
  } catch {}
  return false;
}

function parseSignupError(err: unknown): string | undefined {
  try {
    const parsed = JSON.parse(String((err as any)?.message || ''));
    return parsed?.error;
  } catch {
    const msg = String((err as any)?.message || '').toUpperCase();
    if (msg.includes('USERNAME_TAKEN')) return 'USERNAME_TAKEN';
    if (msg.includes('USERNAME_RESERVED')) return 'USERNAME_RESERVED';
    if (msg.includes('EMAIL_TAKEN')) return 'EMAIL_TAKEN';
    return undefined;
  }
}

/** Retry atomic signup using stored profile (handles resume/idempotent server responses). */
export async function retrySignupFromProfile(profile: SignupProfile): Promise<boolean> {
  // Merge in the full signup profile saved during the initial signup step
  // (template_selection_user) so fields like avatarUrl/coverUrl are always
  // present. The /api/auth/signup endpoint mandates an avatar (AVATAR_REQUIRED),
  // and later steps (e.g. CRM signup template step) only pass identity fields —
  // without this merge those retries 400. CRM owners get an auto-created digital
  // card, so the avatar must be carried through rather than dropped.
  profile = refreshSignupProfileFromStorage(profile);

  const email = String(profile?.email || '').trim();
  const password = String(profile?.password || '');
  if (!email || !password) return false;

  let sessionId: string | undefined;
  try {
    const m = await import('./signupSession');
    sessionId = m.getSignupSessionId();
  } catch {}

  const handle = profile.handle || profile.username || profile.name || email.split('@')[0];
  const username = String(profile.username || handle || '').toLowerCase();

  try {
    const out = await signupAtomic({
      name: profile.name || username,
      email,
      password,
      phone: profile.phone,
      company: profile.company,
      position: profile.position,
      location: profile.location,
      bio: profile.bio,
      avatarUrl: profile.avatarUrl || profile.avatar || null,
      coverUrl: profile.coverUrl || profile.coverImage || null,
      templateKey: profile.templateKey,
      templateId: profile.templateId,
      templateMetaId: profile.templateMetaId,
      preferredHandle: handle,
      desiredUsername: username,
      sessionId,
    } as any);
    if (out?.token) {
      const merged = { ...profile, ...out.user, cardId: out.card?.id };
      persistOwnerSession(out.token, out.user || merged);
      try {
        localStorage.setItem('template_selection_user', JSON.stringify(merged));
      } catch {}
      return true;
    }
  } catch (err) {
    const code = parseSignupError(err);
    if (code === 'EMAIL_TAKEN' || code === 'USERNAME_TAKEN' || code === 'USERNAME_RESERVED') {
      return tryOwnerLogin(email, password);
    }
  }
  return false;
}

/** Ensure a working owner session — validates token, retries signup, then logs in. */
export async function ensureOwnerSessionFromProfile(profile: SignupProfile): Promise<boolean> {
  if (hasOwnerSessionLocal() && (await validateOwnerToken())) {
    return true;
  }

  if (await retrySignupFromProfile(profile)) {
    return true;
  }

  const email = String(profile?.email || '').trim();
  const password = String(profile?.password || '');
  if (email && password && (await tryOwnerLogin(email, password))) {
    return true;
  }

  return hasOwnerSessionLocal() && (await validateOwnerToken());
}

export async function waitForOwnerSession(
  profile: SignupProfile,
  timeoutMs = 10000,
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (await ensureOwnerSessionFromProfile(profile)) return true;
    await new Promise((resolve) => setTimeout(resolve, 250));
  }
  return ensureOwnerSessionFromProfile(profile);
}

const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;

/** Resolve MongoDB user id from props or persisted owner session / signup profile. */
export function resolveOwnerUserId(fallback?: { id?: string; _id?: string }): string | undefined {
  const candidates: Array<string | undefined | null> = [fallback?.id, fallback?._id];
  try {
    const sess = JSON.parse(localStorage.getItem('owner_session') || '{}');
    candidates.push(sess?.id);
    const tpl = JSON.parse(localStorage.getItem('template_selection_user') || '{}');
    candidates.push(tpl?.id, tpl?._id);
  } catch {}
  for (const c of candidates) {
    if (c && OBJECT_ID_RE.test(String(c))) return String(c);
  }
  return undefined;
}

/** Merge signup profile from localStorage with owner_session id. */
export function refreshSignupProfileFromStorage(base?: SignupProfile | null): SignupProfile {
  let profile: SignupProfile = { ...(base || {}) };
  try {
    const raw = localStorage.getItem('template_selection_user');
    if (raw) profile = { ...profile, ...JSON.parse(raw) };
  } catch {}
  try {
    const sessRaw = localStorage.getItem('owner_session');
    if (sessRaw) {
      const sess = JSON.parse(sessRaw);
      if (sess?.id && OBJECT_ID_RE.test(String(sess.id))) {
        profile = { ...profile, id: String(sess.id), _id: String(sess.id) };
      }
    }
  } catch {}
  return profile;
}
